import React, { useState, useEffect } from "react";
import {
  Play,
  Film,
  Youtube,
  ExternalLink,
  Calendar,
  Eye,
  ChevronDown,
} from "lucide-react";
import {
  youtubeService,
  YouTubeVideo,
  YouTubeChannelInfo,
} from "../services/youtubeService";
import PlayfulLoader from "./PlayfulLoader";

interface ToyVideosProps {
  videos?: YouTubeVideo[]; // Make optional since we'll fetch real data
}

export const ToyVideos: React.FC<ToyVideosProps> = ({ videos: propVideos }) => {
  const [videos, setVideos] = useState<YouTubeVideo[]>([]);
  const [channelInfo, setChannelInfo] = useState<YouTubeChannelInfo | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [showAllVideos, setShowAllVideos] = useState(false);

  useEffect(() => {
    const fetchYouTubeData = async () => {
      try {
        setIsLoading(true);
        setHasError(false);

        // Fetch channel info and videos in parallel
        const [channelData, videosData] = await Promise.all([
          youtubeService.getChannelInfo(),
          youtubeService.getVideos(6), // Get 6 videos for homepage
        ]);

        // Only set data if we have real data (not empty)
        if (channelData && videosData.length > 0) {
          setChannelInfo(channelData);
          setVideos(videosData);
        } else {
          setHasError(true);
        }
      } catch (error) {
        console.error("Error fetching YouTube data:", error);
        setHasError(true);
        // Don't use prop videos or any fallback - just mark as error
      } finally {
        setIsLoading(false);
      }
    };

    fetchYouTubeData();
  }, [propVideos]);

  const handleVideoClick = (video: YouTubeVideo) => {
    window.open(video.url, "_blank");
  };

  const handleSubscribeClick = () => {
    window.open(
      "https://www.youtube.com/channel/UCIqJtjSzBtfEu-VQeJ6R5Wg",
      "_blank"
    );
  };

  // Don't render the section if there's an error or no videos
  if (hasError || (!isLoading && videos.length === 0)) {
    return null;
  }

  // Show only first 3 videos initially, or all if showAllVideos is true
  const displayedVideos = showAllVideos ? videos : videos.slice(0, 3);

  if (isLoading) {
    return (
      <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center mb-4">
              <span className="text-4xl mr-2 animate-bounce">🎬</span>
              <Film className="h-8 w-8 text-blue-500 mr-3 animate-pulse" />
              <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-red-500 via-pink-500 to-purple-500 bg-clip-text text-transparent">
                Toy Videos & Reviews
              </h2>
              <Film
                className="h-8 w-8 text-blue-500 ml-3 animate-pulse"
                style={{ animationDelay: "0.5s" }}
              />
              <span
                className="text-4xl ml-2 animate-bounce"
                style={{ animationDelay: "0.3s" }}
              >
                🎥
              </span>
            </div>
          </div>
          <div className="flex justify-center py-12">
            <PlayfulLoader
              size="lg"
              variant="bounce"
              message="🎥 Loading fun videos..."
            />
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="py-16 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <Film className="h-8 w-8 text-blue-500 mr-3" />
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Toy Videos & Reviews
            </h2>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See our toys in action and discover the fun!
          </p>
        </div>

        {/* Video Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {displayedVideos.map((video) => (
            <div
              key={video.id}
              className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden group"
            >
              {/* Video Thumbnail */}
              <div className="relative overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-300"
                />

                {/* Play Button Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-40 transition-all duration-300">
                  <button
                    onClick={() => handleVideoClick(video)}
                    className="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-900 rounded-full p-4 transform hover:scale-110 transition-all duration-200"
                  >
                    <Play className="h-8 w-8 ml-1" />
                  </button>
                </div>

                {/* Duration Badge */}
                <div className="absolute bottom-3 right-3 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                  {video.duration}
                </div>
              </div>

              {/* Video Info */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded-full">
                    YouTube
                  </span>
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(video.publishedAt).toLocaleDateString()}
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-red-600 transition-colors duration-200 line-clamp-2">
                  {video.title}
                </h3>

                <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                  {video.description}
                </p>

                {/* Video Stats */}
                <div className="flex items-center justify-between mb-4 text-sm text-gray-500">
                  <div className="flex items-center">
                    <Eye className="h-4 w-4 mr-1" />
                    {video.viewCount} views
                  </div>
                  <div className="flex items-center">
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {video.channelTitle}
                    </span>
                  </div>
                </div>

                {/* Watch Now Button */}
                <button
                  onClick={() => handleVideoClick(video)}
                  className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-2 px-4 rounded-lg shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
                >
                  <Play className="h-4 w-4 mr-2" />
                  Watch on YouTube
                  <ExternalLink className="h-4 w-4 ml-2" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Show More/Less Button */}
        {videos.length > 3 && (
          <div className="text-center mb-12">
            <button
              onClick={() => setShowAllVideos(!showAllVideos)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              {showAllVideos ? (
                <>
                  Show Less Videos
                  <ChevronDown className="h-4 w-4 ml-2 rotate-180" />
                </>
              ) : (
                <>
                  Show More Videos ({videos.length - 3} more)
                  <ChevronDown className="h-4 w-4 ml-2" />
                </>
              )}
            </button>
          </div>
        )}

        {/* YouTube Channel Banner */}
        <div className="bg-gradient-to-r from-red-500 to-pink-500 rounded-xl p-8 text-center text-white">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-center mb-4">
              <Youtube className="h-12 w-12 mr-3" />
              <h3 className="text-2xl font-bold">
                Subscribe to {channelInfo?.title || "Our YouTube Channel"}!
              </h3>
            </div>

            <p className="text-red-100 text-lg mb-6">
              Get the latest toy reviews, unboxings, and playtime fun!
            </p>

            <button
              onClick={handleSubscribeClick}
              className="bg-white text-red-600 font-semibold py-3 px-8 rounded-full shadow-lg transform hover:scale-105 transition-all duration-200"
            >
              Subscribe Now
            </button>

            {/* Channel Stats */}
            <div className="flex justify-center items-center mt-6 space-x-8 text-sm text-red-100">
              <span>📺 {channelInfo?.videoCount || "500+"} Videos</span>
              <span>
                👥 {channelInfo?.subscriberCount || "50K+"} Subscribers
              </span>
              <span>⭐ 4.9/5 Rating</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToyVideos;
